import { useForm } from '@mantine/form'
import { GetInputProps } from '@mantine/form/lib/types'
import { modals } from '@mantine/modals'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import showModalNotificationError from '@v2/commons/modals/ModalNotification/showModalNotificationError'
import {
  validateLengthBetween,
  validatePassword,
} from '@v2/commons/utils/validators/validators.util'
import { ALL_ROUTES } from '@v2/domains/routing/constants/route-configs.constants'
import { useAppNavigation } from '@v2/domains/routing/hooks/useAppNavigation.hook'

import { SignInDtoV3 } from 'src/api/generated'

import { useSetNewPasswordMutation } from '../api/auth.api'

interface IUseLoginFormUseCase {
  getFormProps: GetInputProps<SignInDtoV3>
  onSubmit: () => void
  isLoading: boolean
  isSuccess: boolean
}

const useSetNewPasswordForm = (): IUseLoginFormUseCase => {
  const [setNewPassword, { isLoading, isSuccess }] = useSetNewPasswordMutation()
  const { navigateTo } = useAppNavigation()

  const form = useForm<{ password: string; confirmPassword: string }>({
    mode: 'uncontrolled',
    initialValues: {
      confirmPassword: '',
      password: '',
    },
    validateInputOnBlur: true,
    validate: {
      password: (value) => {
        const isLengthValid = validateLengthBetween(8, 20, 'password', value)
        return isLengthValid ? isLengthValid : validatePassword(value)
      },
      confirmPassword: (value, values) => {
        if (value !== values.password) {
          return 'Passwords do not match'
        }
        return null
      },
    },
  })

  const onSubmit = () => {
    form.onSubmit(async (values) => {
      await setNewPassword(values.password)
        .unwrap()
        .then(() => {
          form.reset()
          showModalNotification({
            title: 'Password changed successfully',
            buttonText: 'Login',
            path: ALL_ROUTES.LOGIN.path,
            onClose: () => {
              modals.closeAll()
              setTimeout(() => {
                navigateTo(ALL_ROUTES.LOGIN.path)
              }, 0)
            },
          })
        })
        .catch(() => {
          form.reset()
          showModalNotificationError({
            title: 'An error occurred. Please try again.',
            onClose: () => {
              modals.closeAll()
            },
          })
        })
    })()
  }

  return {
    getFormProps: form.getInputProps,
    onSubmit: onSubmit,
    isLoading,
    isSuccess,
  }
}

export default useSetNewPasswordForm
